import { useGetCustomerAccountingQuery } from 'store/queries/bolt-backend/customers'
import { useStoreSelector } from 'hooks/store'
import { useGetLanguage } from 'hooks/use-get-language'
import dayjs from 'dayjs'
import { formatCurrency } from 'utils/format'
import { useTranslation } from 'react-i18next'
import styles from './InvoicesStatusBanner.module.scss'
import { Button, Card, popErrorToast } from '@boltenergy-be/design-system'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton'
import { useState } from 'react'
import { getCustomerBalancePayUrl } from 'lib/customer'
import parse from 'html-react-parser'
import { INVOICE_STATUS_CONFIG } from './constants.ts'
import { getInvoiceStatus } from './utils.ts'
import { InvoicesStatus } from './types.ts'
import classNames from 'classnames'
import { selectCurrentAccount, selectCurrentAccountHasContractWithSubscriptionDateOlderThanOneDay } from 'store/contact/slice.ts'
import { isCustomerInvoice, isCustomerPayment } from 'pages/app/billing/invoices/utils.ts'

const InvoicesStatusBanner = () => {
  // Redux
  const { selected } = useStoreSelector((store) => store.contact)
  const account = useStoreSelector(selectCurrentAccount)
  const hasContractOlderThanOneDay = useStoreSelector(selectCurrentAccountHasContractWithSubscriptionDateOlderThanOneDay)

  // Redux Queries
  const { data, isLoading, isFetching, isError } = useGetCustomerAccountingQuery(
    { customerNumber: selected.account },
    {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: !selected.account || !hasContractOlderThanOneDay
    }
  )

  // Local state
  const [isLoadingBalancePayUrl, setIsLoadingBalancePayUrl] = useState<boolean>(false)

  // i18n
  const { t } = useTranslation(['invoices', 'errors'])
  const language = useGetLanguage()

  // Constants
  const usesDirectDebit = !!account?.paymentDetails?.directDebit

  // -- invoices
  const invoices = data?.documents?.filter((document) => isCustomerInvoice(document))
  const expiredInvoices = invoices?.filter((invoice) => {
    const invoiceDueDate = dayjs(invoice.dueDate)
    return dayjs().isAfter(invoiceDueDate.add(4, 'days'), 'day') && !invoice.paid
  })
  const hasNegativeUnpaidDueInvoices = invoices?.some(
    (invoice) => invoice.amount < 0 && dayjs().isAfter(invoice.dueDate, 'day') && !invoice.paid
  )

  // -- payments
  const hasOpenPayments = data?.documents?.filter((document) => isCustomerPayment(document))?.some((payment) => !payment.paid)
  const showGeneralPaymentButton = hasOpenPayments || hasNegativeUnpaidDueInvoices
  const status = getInvoiceStatus({
    balance: data?.balance,
    usesDirectDebit,
    hasExpiredInvoices: !!expiredInvoices?.length
  })

  /**
   * Handles the click on the general payment button
   * Fetches the balance pay url and opens it
   */
  const onGeneralPaymentButtonClick = async () => {
    setIsLoadingBalancePayUrl(true)

    // Fetch the balance pay url
    const balancePayUrl = await getCustomerBalancePayUrl(selected.account)

    if (balancePayUrl) {
      window.location.href = balancePayUrl
    } else {
      popErrorToast(
        t(($) => $.default, {
          ns: 'errors'
        })
      )
    }

    setIsLoadingBalancePayUrl(false)
  }

  // Data loading
  if (!data && (isLoading || isFetching)) {
    return (
      <Card as="section" className={styles.card}>
        <LoadingSkeleton>
          <LoadingSkeleton.Paragraph lines={2} />
          <LoadingSkeleton.Button />
        </LoadingSkeleton>
      </Card>
    )
  }

  // Don't display the banner if there are BDDUB documents, or if there is an error, or if the status is not applicable
  if (data?.hasBddubDocuments || isError || !status) return null

  return (
    <Card as="section" className={classNames(styles.card, styles[INVOICE_STATUS_CONFIG[status].color])}>
      <Card.Title as="h2" variant="h5" weight="semi-bold">
        {parse(
          t(($) => $.invoicesStatusBanner[INVOICE_STATUS_CONFIG[status].textKey].title, {
            amount: formatCurrency(Number(data?.balance?.customer), { language })
          })
        )}
      </Card.Title>

      {status === InvoicesStatus.DIRECT_DEBIT && <p>{t(($) => $.invoicesStatusBanner.directDebit.text)}</p>}

      {[InvoicesStatus.HAS_UNPAID_INVOICES, InvoicesStatus.DIRECT_DEBIT_WITH_UNPAID_INVOICES].includes(status) &&
        showGeneralPaymentButton && (
          <Button
            variant="layered"
            onClick={onGeneralPaymentButtonClick}
            loading={isLoadingBalancePayUrl}
            isFullwidthMobile
            className={styles.button}
          >
            {t(($) => $.invoicesStatusBanner.open.buttonText)}
          </Button>
        )}
    </Card>
  )
}

export default InvoicesStatusBanner
